import "./Users.css";
import "bootstrap/dist/css/bootstrap.min.css";

import React, { useCallback, useContext, useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";

import userService from "../../services/user-service";
import TextInput from "../../components/TextInput/TextInput";
import NumericPagination from "../../components/NumericPagination/NumericPagination";
import { ColumnHeader, ColumnValue, DefaultTableFooter } from "../../components/DefaultTable/DefaultTable";
import { GlobalContext } from "../GlobalContext";
import { formatToDayMonthYearDate } from "../../utils/date-converter";
import DefaultUserIcon from "../../assets/imgs/default-user-icon.png";
import FilterIcon from "../../assets/imgs/filter-icon.svg";
import CheckedIcon from "../../assets/imgs/checked-icon.svg";
import ChevronRightIcon from "../../assets/imgs/chevron-right-icon.svg";
import { useIsMobile } from "../../hooks/useMediaQuery";

const GENDER_LABELS = {
  MALE: "Masculino",
  FEMALE: "Feminino",
  NON_BINARY: "Não binário",
};

const GENDER_FILTER_OPTIONS = [
  { label: "Masculino", value: "MALE" },
  { label: "Feminino", value: "FEMALE" },
  { label: "Não informado", value: "NOT_INFORMED" },
];

const ROLE_FILTER_OPTIONS = [
  { label: "Usuário", value: "ROLE_USER" },
  { label: "Nutricionista", value: "ROLE_NUTRITIONIST" },
  { label: "Admin", value: "ROLE_ADMIN" },
];

const columnsWidth = {
  IMAGE: 1.5,
  USER: 4,
  GENDER: 2.5,
  BIRTH_DATE: 2.5,
};

const PAGE_SIZE = 7;

const buildSelectedValues = (options) =>
  Object.entries(options)
    .filter(([, selected]) => selected)
    .map(([value]) => value);

const Users = () => {
  const isMobile = useIsMobile();
  const [usersResponse, setUsersResponse] = useState({});
  const [users, setUsers] = useState([]);
  const [currentUsersPage, setCurrentUsersPage] = useState();
  const [searchedName, setSearchedName] = useState("");
  const [selectedGenders, setSelectedGenders] = useState({});
  const [selectedRoles, setSelectedRoles] = useState({});
  const [openFilterDropdown, setOpenFilterDropdown] = useState(false);
  const { setSelectedScreen, setIsLoading } = useContext(GlobalContext);

  const getActiveFilters = useCallback(
    () => ({
      name: searchedName || undefined,
      genders: buildSelectedValues(selectedGenders),
      roles: buildSelectedValues(selectedRoles),
    }),
    [searchedName, selectedGenders, selectedRoles]
  );

  const hasActiveFilters = useCallback(() => {
    const filters = getActiveFilters();
    return Boolean(filters.name) || filters.genders.length > 0 || filters.roles.length > 0;
  }, [getActiveFilters]);

  const fetchUsers = useCallback(
    async (page, filters) => {
      if (page < 0) {
        fetchUsers(0, filters);
        return;
      }

      setIsLoading(true);
      setCurrentUsersPage(page + 1);
      try {
        const response = await userService.getUsers(page, PAGE_SIZE, filters);
        setUsersResponse(response);
        setUsers(response.content);

        if (response.total_pages > 0 && page >= response.total_pages) {
          fetchUsers(response.total_pages - 1, filters);
        }
      } catch (error) {
        console.log("Erro ao buscar usuários", error);
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading]
  );

  const formatGender = (gender) => {
    if (!gender) {
      return "Não informado";
    }
    return GENDER_LABELS[gender] || "—";
  };

  const formatBirthDate = (birthDate) => {
    if (!birthDate) {
      return "—";
    }
    return formatToDayMonthYearDate(birthDate);
  };

  const handleNameSearch = (name) => {
    setSearchedName(name);
    fetchUsers(0, {
      name: name || undefined,
      genders: buildSelectedValues(selectedGenders),
      roles: buildSelectedValues(selectedRoles),
    });
  };

  const toggleGenderFilter = (value) => {
    const next = { ...selectedGenders, [value]: !selectedGenders[value] };
    setSelectedGenders(next);
    fetchUsers(0, {
      name: searchedName || undefined,
      genders: buildSelectedValues(next),
      roles: buildSelectedValues(selectedRoles),
    });
  };

  const toggleRoleFilter = (value) => {
    const next = { ...selectedRoles, [value]: !selectedRoles[value] };
    setSelectedRoles(next);
    fetchUsers(0, {
      name: searchedName || undefined,
      genders: buildSelectedValues(selectedGenders),
      roles: buildSelectedValues(next),
    });
  };

  useEffect(() => {
    document.title = "Usuários";
    setSelectedScreen("users");
    fetchUsers(0, {});
  }, []);

  const isFilterPressed = openFilterDropdown || hasActiveFilters();

  return (
    <>
      <div className="right-side">
        <div className="right-side__content">
          <div className="users__header">
            <h1>
              Usuários
              {usersResponse?.total_elements ? ` (${usersResponse.total_elements})` : ""}
            </h1>
          </div>
          <div className="users">
            <div className="users__content">
              <div className="users__search">
                <div className="users__search__name">
                  <TextInput
                    label="Nome"
                    placeholder="Buscar por nome..."
                    value={searchedName}
                    onChangeText={handleNameSearch}
                  />
                </div>
                <Dropdown
                  show={openFilterDropdown}
                  onToggle={() => setOpenFilterDropdown((prev) => !prev)}
                >
                  <a
                    className={`users__search__filter ${isFilterPressed && "users__search__filter--pressed"}`}
                    href="javascript:;"
                    onClick={() => setOpenFilterDropdown((prev) => !prev)}
                  >
                    <img src={FilterIcon} alt="Filter icon" />
                  </a>
                  <Dropdown.Menu bsPrefix="dropdown-menu users__search__filter__dropdown">
                    <Dropdown.Header>Filtro Avançado</Dropdown.Header>
                    <div className="users__search__filter__dropdown__options">
                      <div className="users__search__filter__dropdown__section">
                        <p className="users__search__filter__dropdown__section__title">Gênero</p>
                        {GENDER_FILTER_OPTIONS.map((option) => (
                          <div className="users__search__filter__dropdown__option" key={option.value}>
                            <a
                              className="users__search__filter__dropdown__option__checkbox"
                              href="javascript:;"
                              onClick={() => toggleGenderFilter(option.value)}
                            >
                              <div
                                className={`users__search__filter__dropdown__option__checkbox__box ${
                                  selectedGenders[option.value] && "checkbox--checked"
                                }`}
                              >
                                <img src={CheckedIcon} alt="Checked icon" />
                              </div>
                              <div className="users__search__filter__dropdown__option__checkbox__label">
                                {option.label}
                              </div>
                            </a>
                          </div>
                        ))}
                      </div>
                      <div className="users__search__filter__dropdown__section">
                        <p className="users__search__filter__dropdown__section__title">Perfil</p>
                        {ROLE_FILTER_OPTIONS.map((option) => (
                          <div className="users__search__filter__dropdown__option" key={option.value}>
                            <a
                              className="users__search__filter__dropdown__option__checkbox"
                              href="javascript:;"
                              onClick={() => toggleRoleFilter(option.value)}
                            >
                              <div
                                className={`users__search__filter__dropdown__option__checkbox__box ${
                                  selectedRoles[option.value] && "checkbox--checked"
                                }`}
                              >
                                <img src={CheckedIcon} alt="Checked icon" />
                              </div>
                              <div className="users__search__filter__dropdown__option__checkbox__label">
                                {option.label}
                              </div>
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              {usersResponse?.total_elements > 0 ? (
                <div className="default-table users__table">
                  <div>
                    <div className="table__header">
                      <ColumnHeader title="Imagem" column="IMAGE" columnsWidth={columnsWidth} />
                      <ColumnHeader title="Usuário" column="USER" columnsWidth={columnsWidth} />
                      <ColumnHeader title="Gênero" column="GENDER" columnsWidth={columnsWidth} />
                      <ColumnHeader title="Data de nascimento" column="BIRTH_DATE" columnsWidth={columnsWidth} />
                    </div>
                    <div className="table__content">
                      {users.map((user) => (
                        <div
                          className="table__row users__table__row"
                          key={user.id}
                          onClick={() => {
                            window.location.href = `/users/${user.id}`;
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              window.location.href = `/users/${user.id}`;
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <ColumnValue column="IMAGE" columnsWidth={columnsWidth}>
                            <img
                              className="users__table__avatar"
                              src={user.photo_url || DefaultUserIcon}
                              alt=""
                            />
                          </ColumnValue>
                          <ColumnValue column="USER" columnsWidth={columnsWidth} className="users__table__user">
                            <p className="users__table__user__name">{user.name}</p>
                            <p className="users__table__user__email">{user.email}</p>
                          </ColumnValue>
                          <ColumnValue
                            column="GENDER"
                            columnsWidth={columnsWidth}
                            className="users__table__field--desktop"
                          >
                            {formatGender(user.gender)}
                          </ColumnValue>
                          <ColumnValue
                            column="BIRTH_DATE"
                            columnsWidth={columnsWidth}
                            className="users__table__field--desktop"
                          >
                            {formatBirthDate(user.birth_date)}
                          </ColumnValue>
                          <div className="users__table__profile-cue" aria-hidden="true">
                            <img src={ChevronRightIcon} alt="" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <DefaultTableFooter>
                    <NumericPagination
                      currentPage={currentUsersPage || 1}
                      totalPages={usersResponse.total_pages}
                      pagesPerView={isMobile ? 5 : 10}
                      onSelectPage={(page) => {
                        fetchUsers(page, getActiveFilters());
                      }}
                    />
                  </DefaultTableFooter>
                </div>
              ) : (
                <div className="users__empty">
                  <p>
                    {hasActiveFilters()
                      ? "Nenhum usuário encontrado para o filtro aplicado."
                      : "Nenhum usuário cadastrado."}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Users;
